<template>
  <li class="relative" :class="[enlargedMode ? enlargedContainerClass : tileContainerClass]">
    <div
      class="
        bg-vice-teal-600
        rounded-full
        w-10
        h-10
        content-center
        justify-center
        absolute
        top-2
        left-2
        z-10
        tooltip
      "
    >
      <ViceSmartphone bgFill="#0d9488" pathFill="white" class="w-full h-8" />
      <span class="tooltip-text ml-2">Smartphone</span>
    </div>

    <!-- Mute -->
    <ToggleMuteStream
      v-if="sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION"
      :isStreamMuted="streamMuted"
      @stream-state-toggled="toggleStreamState"
    />

    <div class="h-full w-full group rounded-2xl block overflow-hidden relative">
      <div class="h-full bg-black">
        <div class="absolute top-0 right-0 z-10" v-if="isConsultationPhase">
          <div class="p-3 relative cursor-pointer" :class="{ 'sm:p-1': !enlargedMode }">
            <div class="relative flex items-center gap-2">
              <div
                data-cy="toggle-focus"
                :class="{ 'sm:p-1': !enlargedMode }"
                class="
                  bg-vice-dark-grey bg-opacity-70
                  text-white
                  rounded-full
                  flex
                  justify-center
                  items-center
                  p-2
                  hover:bg-opacity-90
                "
              >
                <ArrowsExpandIcon
                  class="h-6 w-6"
                  v-if="!enlargedMode"
                  :class="{ 'sm:w-5 sm:h-5': !enlargedMode }"
                  @click="onEnlargeIconClick(tileId)"
                />
                <ArrowsCollapseIcon class="p-0.5" @click="onMinimizeIconClick" v-else />
              </div>
            </div>
          </div>
        </div>

        <StreamPlayerRemote
          :streamMuted="streamMuted"
          :isAudioMuted="false"
          :jitsiUserId="userJitsiId"
          :setVideoAudioRefs="setVideoAudioRefs"
        />

        <span v-if="streamMuted" class="muted-text w-full items-center justify-center rounded-xl" />
        <span
          v-if="streamMuted"
          class="muted-text w-full items-center justify-center rounded-xl z-5"
        >
          PAUSED
        </span>

        <UserLabel displayName="Smartphone" :hideStats="true">
          <ViceSmartphone class="h-6 w-6 -my-1 mr-1 shadow-filter" bgFill="#000" />
        </UserLabel>

        <SpeakingIndicator :audioLevel="remoteDisplayInfo.audioLevel" />
      </div>
    </div>
  </li>
</template>

<script>
import { computed, onUnmounted, ref } from 'vue';
import { ArrowsExpandIcon } from '@heroicons/vue/outline';
import UserLabel from '@/Components/Session/Grid/UserLabel.vue';
import useSessionStore from '@/Stores/useSessionStore';
import StreamPlayerRemote from '@/Components/Session/Grid/StreamPlayerRemote.vue';
import ArrowsCollapseIcon from '@/Components/Icons/ArrowsCollapseIcon.vue';
import { VICE_SESSION_PHASES } from '@/Utils/vice-constants';
import ViceSmartphone from '@/Components/Icons/ViceSmartphone.vue';
import ToggleMuteStream from '@/Components/Inputs/ToggleMuteStream.vue';
import SpeakingIndicator from '@/Components/Session/Grid/SpeakingIndicator.vue';

export default {
  computed: {
    VICE_SESSION_PHASES() {
      return VICE_SESSION_PHASES;
    },
  },
  props: {
    userJitsiId: {
      type: [Number, String],
    },
    remoteDisplayInfo: {
      type: Object,
      required: true,
    },

    setVideoAudioRefs: {
      type: Function,
      required: true,
    },
    tileId: {
      type: String,
      required: false,
    },
    gridDimension: {
      type: Object,
      required: true,
    },
    enlargedJitsiId: {
      type: String,
    },
    index: {
      type: [Number, String],
      required: true,
    },
    muteMedia: {
      type: Function,
    },
  },
  emits: ['toggleEnlargeMode'],
  components: {
    SpeakingIndicator,
    ToggleMuteStream,
    ViceSmartphone,
    UserLabel,
    StreamPlayerRemote,
    ArrowsExpandIcon,
    ArrowsCollapseIcon,
  },
  setup(props, { emit }) {
    const streamMuted = ref(false);
    const sessionStore = useSessionStore();
    const shouldDisplaySmartphoneStream = ref(true);

    const toggleSmartphoneStream = () => {
      shouldDisplaySmartphoneStream.value = !shouldDisplaySmartphoneStream.value;
    };

    const tileContainerClass = computed(
      () =>
        `col-start-${props.gridDimension[props.index].start ?? 1} col-span-${
          props.gridDimension[props.index].cols
        } row-span-${props.gridDimension[props.index].rows}`,
    );
    const enlargedContainerClass = 'col-start-2 col-span-10 row-span-8 row-start-1';
    const enlargedMode = computed(() => props.enlargedJitsiId === props.userJitsiId);

    const isConsultationPhase = ref(sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION);

    const onEnlargeIconClick = () => {
      emit('toggleEnlargeMode', props.userJitsiId);
    };

    const onMinimizeIconClick = () => {
      emit('toggleEnlargeMode');
    };

    onUnmounted(() => {
      if (enlargedMode.value) onMinimizeIconClick();
    });

    const toggleStreamState = (e) => {
      streamMuted.value = e;
      props.muteMedia(props.userJitsiId, e);
    };

    return {
      streamMuted,
      sessionStore,
      enlargedMode,
      tileContainerClass,
      isConsultationPhase,
      enlargedContainerClass,
      toggleStreamState,
      onEnlargeIconClick,
      onMinimizeIconClick,
      toggleSmartphoneStream,
    };
  },
};
</script>
<style scoped></style>
